import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { Alert, Col, Form, Row, Spinner } from "react-bootstrap";
import { useAuth } from "../context/userAuth/UserAuthState";
import {
  createUserAPI,
  deleteUserAPI,
  fetchUserListAPI,
  updateMyProfileAPI,
  updateUserAPI,
} from "../api/api";

const emptyUserForm = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  is_user: "manager",
};

const PRO_SUPER_ADMIN_EMAIL = "ahsanrahman745@gmail.com";
const isSuperRole = (role) => role === "isAdmin" || role === "super_admin" || role === "pro_super_admin";
const isProSuperAdmin = (item) => item?.email?.toLowerCase() === PRO_SUPER_ADMIN_EMAIL;

const SettingsPage = () => {
  const { user, updateSessionUser } = useAuth();
  const token = user?.authorization;
  const currentUser = user?.user || {};
  const isSuperAdmin = isSuperRole(currentUser.is_user);

  const [profileForm, setProfileForm] = useState({
    first_name: currentUser.first_name || "",
    last_name: currentUser.last_name || "",
    old_password: "",
    password: "",
  });
  const [profileMessage, setProfileMessage] = useState("");
  const [profileError, setProfileError] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [editingId, setEditingId] = useState(null);
  const [usersError, setUsersError] = useState("");
  const [usersMessage, setUsersMessage] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const editingSuperAdmin = isSuperRole(userForm.is_user);

  const loadUsers = async () => {
    if (!isSuperAdmin) return;
    setUsersLoading(true);
    const res = await fetchUserListAPI(token);
    if (res.success) setUsers(res.users || []);
    else setUsersError(res.message || "Failed to load users.");
    setUsersLoading(false);
  };

  useEffect(() => { loadUsers(); }, []); // eslint-disable-line

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileMessage("");
    setProfileLoading(true);

    const body = {
      first_name: profileForm.first_name,
      last_name: profileForm.last_name,
    };
    if (profileForm.password) {
      body.old_password = profileForm.old_password;
      body.password = profileForm.password;
    }

    const res = await updateMyProfileAPI(body, token);
    setProfileLoading(false);

    if (res.success) {
      updateSessionUser(res.user);
      setProfileForm((prev) => ({ ...prev, old_password: "", password: "" }));
      setProfileMessage("Profile updated.");
    } else {
      setProfileError(res.message || "Failed to update profile.");
    }
  };

  const resetUserForm = () => {
    setUserForm(emptyUserForm);
    setEditingId(null);
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setUsersError("");
    setUsersMessage("");
    setUsersLoading(true);

    if (isProSuperAdmin({ email: userForm.email })) {
      setUsersLoading(false);
      setUsersError("This protected admin user is created by the seeder only.");
      return;
    }

    const apiCall = editingId ? updateUserAPI : createUserAPI;
    const body = editingId ? { ...userForm, id: editingId } : userForm;
    if (editingId && !body.password) {
      delete body.password;
      delete body.old_password;
    }

    const res = await apiCall(body, token);
    setUsersLoading(false);

    if (res.success) {
      setUsersMessage(editingId ? "User updated." : "User added.");
      resetUserForm();
      loadUsers();
    } else {
      setUsersError(res.message || "Failed to save user.");
    }
  };

  const startEditUser = (item) => {
    if (isProSuperAdmin(item)) return;
    setEditingId(item.id);
    setUserForm({
      first_name: item.first_name || "",
      last_name: item.last_name || "",
      email: item.email || "",
      password: "",
      old_password: "",
      is_user: item.is_user || "manager",
    });
  };

  const handleDeleteUser = async (item) => {
    if (!window.confirm(`Delete user ${item.email}?`)) return;
    setUsersError("");
    setUsersMessage("");
    const res = await deleteUserAPI({ id: item.id }, token);
    if (res.success) {
      setUsersMessage("User deleted.");
      loadUsers();
    } else {
      setUsersError(res.message || "Failed to delete user.");
    }
  };

  return (
    <DashboardLayout pageTitle="Settings">
      <div className="cp-header">
        <div>
          <h2 className="cp-header__title">Settings</h2>
          <p className="cp-header__sub">Manage your account and team access.</p>
        </div>
      </div>

      <div className="ac-form-card">
        <h3 className="db-card__title">My Profile</h3>
        {profileError && <Alert variant="danger">{profileError}</Alert>}
        {profileMessage && <Alert variant="success">{profileMessage}</Alert>}
        <Form onSubmit={handleProfileSubmit}>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">First Name</Form.Label>
                <Form.Control className="cp-form-control" value={profileForm.first_name} onChange={(e) => setProfileForm((p) => ({ ...p, first_name: e.target.value }))} required />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Last Name</Form.Label>
                <Form.Control className="cp-form-control" value={profileForm.last_name} onChange={(e) => setProfileForm((p) => ({ ...p, last_name: e.target.value }))} required />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Current Password</Form.Label>
                <Form.Control className="cp-form-control" type="password" value={profileForm.old_password} onChange={(e) => setProfileForm((p) => ({ ...p, old_password: e.target.value }))} placeholder="Required to change password" required={!!profileForm.password} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">New Password</Form.Label>
                <Form.Control className="cp-form-control" type="password" value={profileForm.password} onChange={(e) => setProfileForm((p) => ({ ...p, password: e.target.value }))} placeholder="Leave blank to keep current" />
              </Form.Group>
            </Col>
          </Row>
          <button className="cp-modal-submit" type="submit" disabled={profileLoading}>
            {profileLoading ? <Spinner size="sm" animation="border" /> : "Update Profile"}
          </button>
        </Form>
      </div>

      {isSuperAdmin && (
        <div className="ac-form-card">
          <h3 className="db-card__title">{editingId ? "Edit User" : "Add User"}</h3>
          {usersError && <Alert variant="danger">{usersError}</Alert>}
          {usersMessage && <Alert variant="success">{usersMessage}</Alert>}
          <Form onSubmit={handleUserSubmit}>
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label className="cp-form-label">First Name</Form.Label>
                  <Form.Control className="cp-form-control" value={userForm.first_name} onChange={(e) => setUserForm((p) => ({ ...p, first_name: e.target.value }))} required />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label className="cp-form-label">Last Name</Form.Label>
                  <Form.Control className="cp-form-control" value={userForm.last_name} onChange={(e) => setUserForm((p) => ({ ...p, last_name: e.target.value }))} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label className="cp-form-label">Email</Form.Label>
                  <Form.Control className="cp-form-control" type="email" value={userForm.email} onChange={(e) => setUserForm((p) => ({ ...p, email: e.target.value }))} required disabled={editingId && editingSuperAdmin} />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label className="cp-form-label">Role</Form.Label>
                  <Form.Select className="cp-form-control" value={userForm.is_user} onChange={(e) => setUserForm((p) => ({ ...p, is_user: e.target.value }))} disabled={editingId && editingSuperAdmin}>
                    <option value="manager">Manager</option>
                    <option value="isAdmin">Super Admin</option>
                    {userForm.is_user === "super_admin" && <option value="super_admin">Super Admin</option>}
                    {userForm.is_user === "pro_super_admin" && <option value="pro_super_admin"></option>}
                  </Form.Select>
                </Form.Group>
              </Col>
              {editingId && (
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">Your Password</Form.Label>
                    <Form.Control className="cp-form-control" type="password" value={userForm.old_password || ""} onChange={(e) => setUserForm((p) => ({ ...p, old_password: e.target.value }))} placeholder="Required if changing password" required={!!userForm.password} />
                  </Form.Group>
                </Col>
              )}
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label className="cp-form-label">{editingId ? "New Password" : "Password"}</Form.Label>
                  <Form.Control className="cp-form-control" type="password" value={userForm.password} onChange={(e) => setUserForm((p) => ({ ...p, password: e.target.value }))} placeholder={editingId ? "Blank keeps current" : ""} required={!editingId} />
                </Form.Group>
              </Col>
            </Row>
            <button className="cp-modal-submit" type="submit" disabled={usersLoading}>
              {usersLoading ? <Spinner size="sm" animation="border" /> : editingId ? "Update User" : "Add User"}
            </button>
            {editingId && <button className="cp-modal-cancel ms-2" type="button" onClick={resetUserForm}>Cancel</button>}
          </Form>

          <div className="cp-table-wrap mt-4">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((item) => (
                  <tr key={item.id}>
                    <td>{item.first_name} {item.last_name}</td>
                    <td>{item.email}</td>
                    <td>{isProSuperAdmin(item) ? "" : <span className="cp-type-badge">{isSuperRole(item.is_user) ? "Super Admin" : "Manager"}</span>}</td>
                    <td>
                      {item.id === currentUser.id || isProSuperAdmin(item) ? (
                        <span className="cp-author">{item.id === currentUser.id ? "Use My Profile" : "Protected"}</span>
                      ) : (
                        <button className="cp-edit-btn" onClick={() => startEditUser(item)}>Edit</button>
                      )}
                      {item.id !== currentUser.id && !isProSuperAdmin(item) && (
                        <button className="cp-delete-btn" onClick={() => handleDeleteUser(item)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SettingsPage;
